import { Button, Tooltip } from "@mui/material";
import React, { useContext, useState } from "react";
import { map } from "lodash/fp";
import { isEqual } from "lodash";
import CreateUserModal from "./CreateUserModal";
import UploadUsersModal from "./UploadUsersModal";
import RemoveResourceModal from "./RemoveResourceModal";
import { useMutation } from "@apollo/client/react";
import { EssealDataTable } from "esseal-data-table";
import usersColumns from "./usersColumns";
import { GetAllEmployeesQuery, UpdateEmployeeDocument, User, EmploymentStatusEnum, PermissionEntityEnum, PermissionActionEnum } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import PermissionContext from "@/Contexts/Permissions";
import CurrentUserContext from "@/Contexts/CurrentUser";
import RequestPermissionTooltip from "@/components/RequestPermissionTooltip";

interface UsersTableProps {
  loading: boolean;
  data: GetAllEmployeesQuery["getEmployees"] | undefined;
}

const mapNode = map("node");

const UsersTable: React.FC<UsersTableProps> = ({
  loading,
  data
}) => {
  const { currentUser } = useContext(CurrentUserContext);
  const { Manage, Create } = PermissionActionEnum;
  const { TagManagement, EmployeeManagement } = PermissionEntityEnum;
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [resourceData, setResourceData] = useState<{ resourceId: string, openModal: boolean } | null>(null);

  const [updateEmployee] = useMutation(UpdateEmployeeDocument, {
    onCompleted: () => displayNotification({ type: "success", text: "Resource updated" }),
    onError: () => displayNotification({ type: "error", text: "Resource could not be updated" }),
    refetchQueries: ["getAllEmployees"]
  });

  const handleOnClickRemoveResource = (resource: User) => {
    if (resource.hasFutureSchedules) {
      setResourceData({ resourceId: resource.companyEmployment?.id as string, openModal: true });
    } else {
      const newStatus = resource.companyEmployment?.status === EmploymentStatusEnum.Active ?
        EmploymentStatusEnum.Inactive :
        EmploymentStatusEnum.Active;

      updateEmployee({
        variables: {
          user: {
            id: resource.companyEmployment?.id as string,
            companyEmploymentStatus: newStatus
          }
        }
      });
    }
  };

  const handleOnUpdateEmployee = (updatedRow: User, originalRow: User) => {
    if (isEqual(updatedRow.companyEmployment?.loginEnabled, originalRow.companyEmployment?.loginEnabled)) return;

    updateEmployee({
      variables: {
        user: { id: updatedRow.companyEmployment?.id as string, loginEnabled: updatedRow.companyEmployment?.loginEnabled }
      }
    });
  }

  return (
    <React.Fragment>
      <EssealDataTable
        rows={mapNode(data?.edges) as User[]}
        columns={
          usersColumns(
            canPerformAction(Manage, TagManagement),
            handleOnClickRemoveResource,
            handleOnUpdateEmployee,
            currentUser?.id
          )}
        loading={loading}
        checkboxSelection={false}
        pagination
        pageSize={25}

        toolbar={
          <React.Fragment>
            <RequestPermissionTooltip
              hasPermission={canPerformAction(Create, EmployeeManagement)}
              requiredPermission={{ action: Create, entity: EmployeeManagement }}
              checkRequestExists={checkRequestExists}
              onRequest={requestPermission}
            >
              <Button
                onClick={() => setShowCreateModal(true)}
                disabled={!canPerformAction(Create, EmployeeManagement)}
              >
                + New Person
              </Button>
            </RequestPermissionTooltip>
            <Tooltip title="Future feature">
              <span>
                <Button
                  onClick={() => setShowUploadModal(true)}
                  // disabled={!canPerformAction(Create, EmployeeManagement)}
                  disabled // TODO: Switch out old implementation with new one (used on Welcome)
                >
                  Bulk Add People
                </Button>
              </span>
            </Tooltip>
          </React.Fragment>
        }
      />

      {showCreateModal && <CreateUserModal handleClose={() => setShowCreateModal(false)} />}
      {showUploadModal && <UploadUsersModal handleClose={() => setShowUploadModal(false)} />}
      {resourceData && <RemoveResourceModal employees={mapNode(data?.edges)} resourceId={resourceData.resourceId} handleClose={() => setResourceData(null)} />}
    </React.Fragment>
  )
}

export default UsersTable;
